// src/pages/hooks/useBusinessFiles.ts
import { useCallback, useEffect, useRef, useState } from "react";
import {
  adriva_listBusinessFiles,
  adriva_uploadBusinessFiles,
  adriva_deleteBusinessFile,
  BusinessFileManifestEntry,
} from "../API";

export interface PendingFile {
  /** Local File object (not yet uploaded) */
  file: File;
  /** Upload state */
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
}

interface UseBusinessFilesReturn {
  /** Files already in storage for this app */
  saved:         BusinessFileManifestEntry[];
  /** Files staged locally, not yet uploaded */
  pending:       PendingFile[];

  loading:       boolean;
  uploading:     boolean;
  error:         string | null;

  /** Stage files for upload (doesn't upload yet) */
  addFiles:      (files: File[]) => void;
  /** Remove a staged-but-not-uploaded file */
  removePending: (name: string) => void;
  /** Upload all staged files to the server. Clears pending on success. */
  uploadPending: () => Promise<void>;
  /** Delete a saved file from the server */
  deleteSaved:   (name: string) => Promise<void>;
  /** Reload saved list from server */
  refresh:       () => Promise<void>;
}

export function useBusinessFiles(
  appNm: string | undefined,
): UseBusinessFilesReturn {
  const [saved,     setSaved]     = useState<BusinessFileManifestEntry[]>([]);
  const [pending,   setPending]   = useState<PendingFile[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!appNm?.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await adriva_listBusinessFiles(appNm);
      if (mountedRef.current) setSaved(r.files || []);
    } catch (e: any) {
      if (mountedRef.current) setError(e?.message || "Failed to load files.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [appNm]);

  // Auto-load when appNm becomes available
  useEffect(() => {
    if (appNm?.trim()) refresh();
    else setSaved([]);
  }, [appNm, refresh]);

  const addFiles = useCallback((files: File[]) => {
    setPending(prev => {
      const next = [...prev];
      for (const f of files) {
        if (!next.find(p => p.file.name === f.name)) {
          next.push({ file: f, status: "pending" });
        }
      }
      return next;
    });
  }, []);

  const removePending = useCallback((name: string) => {
    setPending(prev => prev.filter(p => p.file.name !== name));
  }, []);

  const uploadPending = useCallback(async () => {
    if (!appNm?.trim() || pending.length === 0) return;

    const toUpload = pending.filter(p => p.status === "pending");
    if (toUpload.length === 0) return;

    setUploading(true);
    setError(null);

    // Mark all as uploading
    setPending(prev =>
      prev.map(p => p.status === "pending" ? { ...p, status: "uploading" } : p)
    );

    try {
      const r = await adriva_uploadBusinessFiles(
        appNm,
        toUpload.map(p => p.file)
      );

      // Mark each as done or error
      const errorMap: Record<string, string> = {};
      for (const e of r.errors || []) errorMap[e.name] = e.error;

      setPending(prev =>
        prev.map(p => ({
          ...p,
          status: p.status === "uploading"
            ? (errorMap[p.file.name] ? "error" : "done")
            : p.status,
          error: errorMap[p.file.name],
        }))
      );

      // Clear successfully uploaded from pending after a short delay
      setTimeout(() => {
        if (mountedRef.current) {
          setPending(prev => prev.filter(p => p.status !== "done"));
        }
      }, 1500);

      // Refresh saved list
      await refresh();

      if (r.errors?.length) {
        setError(
          `${r.errors.length} file(s) failed to upload: ` +
          r.errors.map((e: any) => e.name).join(", ")
        );
      }
    } catch (e: any) {
      setError(e?.message || "Upload failed.");
      setPending(prev =>
        prev.map(p => p.status === "uploading"
          ? { ...p, status: "error", error: "Upload failed" }
          : p
        )
      );
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  }, [appNm, pending, refresh]);

  const deleteSaved = useCallback(async (name: string) => {
    if (!appNm?.trim()) return;
    try {
      await adriva_deleteBusinessFile(appNm, name);
      setSaved(prev => prev.filter(f => f.name !== name));
    } catch (e: any) {
      setError(e?.message || "Delete failed.");
    }
  }, [appNm]);

  return {
    saved, pending,
    loading, uploading, error,
    addFiles, removePending, uploadPending, deleteSaved, refresh,
  };
}